/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Container, 
    Grid, 
    Typography, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableRow,
    Button,
    Box, 
    CircularProgress
} from '@material-ui/core'
import {
    generateCredentials,
    generateToken,
    updateAppKeys,
    removeCredentials,
    fetchIDPs,
    removeDefaultApplicationKeys,
} from '../../../data/operations';
import { isValidData } from '../../../data/utils';
import { useStyles, CustomButton } from './style';
import Settings from '../../../conf/settings';
import {
    ApplicationState,
    Api,
    CredentialsReducer,
    Tier,
    UserReducer,
    SubscriptionList,
    Subscription,
    IDPReducer,
    KeyType,
    Key,
} from "../../../data/interfaces";
import CopyTextField from '../../../elements/CopyTextField';
import PasswordCopyField from '../../../elements/PasswordCopyField';
import GreyOutlinedButton from '../../../elements/GreyOutlinedButton';
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";
import { setErrorNotification } from '../../../store/actions/notification';
import {
    ASGARDEO_KEY_MANAGER_TYPE, CLIENT_CREDENTIALS_GRANT_TYPE, DEFAULT_APP_INTERNAL_PREFIX,
    DEFAULT_ASGARDEO_KEY_MANAGER_PREFIX, RESOURCE_AUTHORIZE, RESOURCE_TOKEN, SUBSCRIPTION_THROTTLING_POLICY_UNLIMITED, VALUE_ZERO
} from "../../../data/constants";
import CurlDialog from "../../shared/CurlDialog/CurlDialog";
import CustomIcons from '../../../icons/CustomIcons';
import { ConfirmationDialog } from "../../shared/ConfirmationDialog/ConfirmationDialog";
import {
    trackAPICredentialsRemoveClick,
    trackCredentialGenerate
} from "../../../util/app-insights";
import { createOauthTokenSuccess } from '../../../store/actions/applicationKeys';
import AccessTokenDialog from '../../shared/AccessTokenDialog/AccessTokenDialog';
import { matchPath, useLocation } from 'react-router-dom';
import { ApiCredentialUrlParams } from '../../shared/MainMenuDrawer/MainMenuItems';
import Notification from '../../ChoreoSystem/Notification/Notification';

interface OAuthCredentialsProps {
    apiInfo: Api,
    credentialsReducer: CredentialsReducer,
    userInfo: UserReducer,
    subscriptionsInfo: SubscriptionList,
    idpReducer: IDPReducer,
    dispatchGenerateCreds: (apiId: string, appName: string, organizationId: string, policy: string,
        defaultSelectedGrantTypeValues: string[], callBackUrl: string, throttlingPolicy: string, keyType: KeyType,
        keyManager: string) => void,
    dispatchRemoveCreds: (appName: string) => void,
    dispatchFetchIDPs: (organizationId: string) => void;
    dispatchRemoveDefaultApplicationKeys: (keyType: KeyType, appName: string) => void;
}

function OAuthCredentials(props: OAuthCredentialsProps) {
    const intl = useIntl();
    const classes = useStyles();
    const dispatch = useDispatch();
    const {
      dispatchGenerateCreds,
      dispatchRemoveCreds,
      dispatchFetchIDPs,
      dispatchRemoveDefaultApplicationKeys,
      apiInfo,
      userInfo,
      idpReducer,
      subscriptionsInfo,
      credentialsReducer,
    } = props;
    const { isKeyFetching, isKeyGenerating, isKeyDeleting, keys: defaultAppKeys } = credentialsReducer;
    const idpList = idpReducer?.idps.list;
    const defaultSelectedGrantTypeValues = [CLIENT_CREDENTIALS_GRANT_TYPE];
    const [policy, setPolicy] = useState('');
    const [curlOpen, setCurlOpen] = useState(false);
    const [tokenOpen, setTokenOpen] = useState(false);
    const [callBackUrl, setcallBackUrl] = useState("");
    const [token, setToken] = useState('');
    const [isTokenGenerating, setIsTokenGenerating] = useState(false);
    const [removeCredentialsOpen, setRemoveCredentialsOpen] = useState(false);
    const [values, setValues] = useState({
        consumerKey: '',
        consumerSecret: '',
        showconsumerSecret: false,
    });
    const [keyManagerName, setKeyManagerName] = useState(Settings.keyManager);
    const base64Encoded = window.btoa(values.consumerKey + ':' + values.consumerSecret);
    const [permissions ,setPermissions] = useState<string[]>([]);
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>(permissions);
    const [generatedScopesForToken, setGeneratedScopesForToken] = useState<string[]>(permissions);
    const location = useLocation();
    const [keyType, setKeyType] = useState<KeyType>(KeyType.SANDBOX);
    const [envKey, setEnvKey] = useState<Key>();

    let apiId = '';
    let apiName = '';
    let appName = '';
    let userName = '';
    let organizationId = '';
    let defaultAsgardeoKeyManagerName = '';
    let defaultSubscription: Subscription | undefined;

    useEffect(()=>{
        const currentPath = location.pathname;
        const { params } = matchPath<ApiCredentialUrlParams>(currentPath, "/:orgName/apis/:apiId/:page/:env") || {};
        if (params?.env) {
            setKeyType(params.env.toUpperCase() as KeyType);
        }
    }, [location])

    useEffect(() => {
        if (isValidData(apiInfo)) {
            dispatchFetchIDPs(organizationId);
        }
        setPermissions(apiInfo.scopes ? apiInfo.scopes.map((scope) => scope.key) : [])
        setGeneratedScopesForToken(apiInfo.scopes ? apiInfo.scopes.map((scope) => scope.key) : [])
        setSelectedPermissions(apiInfo.scopes ? apiInfo.scopes.map((scope) => scope.key) : [])
    }, [apiInfo])

    if (isValidData(apiInfo)) {
        apiId = apiInfo.id;
        apiName = apiInfo.name;
    }

    if (isValidData(userInfo.userInfo) && userInfo.selectedOrg.uuid) {
        userName = userInfo.userInfo.sub;
        organizationId = userInfo.selectedOrg.uuid;
        defaultAsgardeoKeyManagerName = DEFAULT_ASGARDEO_KEY_MANAGER_PREFIX + userInfo.selectedOrg.handle;
    }

    if (isValidData(subscriptionsInfo) && userName !== "" && apiName !== "") {
      const defaultAppName =
        DEFAULT_APP_INTERNAL_PREFIX + userName + "_" + apiName;
      defaultSubscription = subscriptionsInfo.list.find(
        (subscription: Subscription) =>
          subscription.applicationInfo?.name === defaultAppName
      );
    }

    useEffect(() => {
        const filteredEnvKey = defaultAppKeys?.list && defaultAppKeys.list.find((key) => key.keyType === keyType);
        setEnvKey(filteredEnvKey);
        if (filteredEnvKey) {
            setValues({
                consumerKey: filteredEnvKey.consumerKey,
                consumerSecret: filteredEnvKey.consumerSecret,
                showconsumerSecret: false,
            });
            setcallBackUrl(filteredEnvKey.callbackUrl);
            setKeyManagerName(filteredEnvKey.keyManager);
        }
    }, [defaultAppKeys, keyType])

    useEffect(() => {
        if (apiInfo?.tiers?.length > 0) {
            if (apiInfo.tiers.find((tier: Tier) => tier.tierName === SUBSCRIPTION_THROTTLING_POLICY_UNLIMITED)) {
                setPolicy(SUBSCRIPTION_THROTTLING_POLICY_UNLIMITED);
            } else {
                setPolicy(apiInfo?.tiers[VALUE_ZERO]?.tierName);
            }
        }
    }, [apiInfo.tiers])

    const fetchAccessToken = async () => {
        await generateAccessToken(selectedPermissions);
    };

    const isKeyFromAsgardeoKM = (keyManagerName === defaultAsgardeoKeyManagerName);
    const isResidentKeyManagerNotUsed = !envKey || isKeyFromAsgardeoKM;
    const isAsgardeoKeyManagerAvailable = (idpList.filter((idp) => idp.type === ASGARDEO_KEY_MANAGER_TYPE &&
        idp.name === defaultAsgardeoKeyManagerName).length) > 0;
    const tokenEndpoint = isAsgardeoKeyManagerAvailable && isResidentKeyManagerNotUsed ? idpList
        .filter((idp) => idp.type === ASGARDEO_KEY_MANAGER_TYPE &&
        idp.name === defaultAsgardeoKeyManagerName)[0].tokenEndpoint : Settings.tokenEndpoint;
    const revokeEndpoint = isAsgardeoKeyManagerAvailable && isResidentKeyManagerNotUsed ? idpList
        .filter((idp) => idp.type === ASGARDEO_KEY_MANAGER_TYPE &&
        idp.name === defaultAsgardeoKeyManagerName)[0].revokeEndpoint : Settings.revokeEndpoint;
    const authorizeEndpoint = isAsgardeoKeyManagerAvailable && isResidentKeyManagerNotUsed ?
        tokenEndpoint.replace(RESOURCE_TOKEN, RESOURCE_AUTHORIZE) : Settings.tokenEndpoint
        .replace(RESOURCE_TOKEN, RESOURCE_AUTHORIZE);

    /**
    * Genrates access token for a given app
    */
    const generateAccessToken = async (selectedPermissions: string[]) => {
        try {
            if (envKey) {
                const response = await generateToken(
                    selectedPermissions,
                    defaultSubscription?.applicationId as string,
                    envKey
                );
                setToken(response?.data.accessToken);
                setGeneratedScopesForToken(response?.data.tokenScopes);
                setIsTokenGenerating(false);
                setTokenOpen(true);
            }
        } catch (error) {
            dispatch(setErrorNotification(intl.formatMessage({
                id: "api.apiCredentials.messages.errorTokenGeneration",
                defaultMessage: "An error occurred during token generation."
            })));
        }
    }

    const handleGenerateCredentialsClick = () => {
        //Re-initalizing popup dialog alert for instances when component doesn't remount/refresh
        setRemoveCredentialsOpen(false);
        trackCredentialGenerate(apiName, apiId, defaultSelectedGrantTypeValues);

        if (userName !== '' && apiName !== '' && policy !== '') {
            // Construct application name
            appName = DEFAULT_APP_INTERNAL_PREFIX + userName + "_" + apiName;
            const keyManager = isResidentKeyManagerNotUsed && isAsgardeoKeyManagerAvailable ?
                defaultAsgardeoKeyManagerName : Settings.keyManager;
            dispatchGenerateCreds(apiId, appName, organizationId, policy, defaultSelectedGrantTypeValues,
                callBackUrl || "", Settings.defaultAppThrottlingPolicy, keyType, keyManager);
        } else {
            dispatch(setErrorNotification(intl.formatMessage({
                id: "api.apiCredentials.messages.errorOrgUserApi",
                defaultMessage: "Missing organization, username, subscription plan or API name"
            })));
        }
    }

    // Todo: Use this function after https://github.com/wso2-enterprise/choreo/issues/20612 is fixed
    // const handleRemoveCredentialsClick = () => {
    //     appName = DEFAULT_APP_INTERNAL_PREFIX + userName + "_" + apiName;
    //     // Check whether the default application has only one key.
    //     // If so, remove the subscription and the default application.
    //     // Otherwise, remove only the key.
    //     if (defaultAppKeys && defaultAppKeys.list.length === 1) {
    //         dispatchRemoveCreds(appName);
    //     } else {
    //         dispatchRemoveDefaultApplicationKeys(keyType, appName);
    //     }
    // }

    const handleRemoveCredentialsClick = () => {
        appName = DEFAULT_APP_INTERNAL_PREFIX + userName + "_" + apiName;
        dispatchRemoveCreds(appName);
    }

    const setPermissionsCallback = (permissions: string[]) => {
        setSelectedPermissions(permissions);
    }

    const handleClickCurlOpen = () => {
        setCurlOpen(true);
    };

    const handleClickCurlClose = () => {
        setCurlOpen(false);
        setPermissions(apiInfo.scopes ? apiInfo.scopes.map((scope) => scope.key) : []);
        setSelectedPermissions(apiInfo.scopes ? apiInfo.scopes.map((scope) => scope.key) : []);
    };

    const handleClickTokenOpen = () => {
        fetchAccessToken();
        setTokenOpen(true);
    };

    const handleClickTokenClose = () => {
        setTokenOpen(false);
        setToken('');
        setSelectedPermissions(permissions);
        setIsTokenGenerating(false);
        dispatch(createOauthTokenSuccess({ accessToken: '', tokenScopes: [], validityTime: 0 }));

    };

    const handleClickGenerate = () => {
        if (selectedPermissions.length !== 0) {
            setPermissions(selectedPermissions);
        }
        setToken('');
        setIsTokenGenerating(true);
        generateAccessToken(selectedPermissions);     
    };

    const handleClickRemoveCredentialsOpen = () => {
        setRemoveCredentialsOpen(true);
        trackAPICredentialsRemoveClick(apiId);
    };

    const handleClickRemoveCredentialsClose = () => {
        setRemoveCredentialsOpen(false);
    };

    return (
        <main className={classes.content}>
            {isKeyFetching || isKeyGenerating || isKeyDeleting ?
                <div className={classes.loaderContainer}>
                    <PreLoaderLogo size="medium" />
                </div> :
                <Container maxWidth="lg" className={classes.container} classes={{ root: classes.componentPadding }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={12} lg={11}>
                            <Box className={classes.table}>
                                <Notification color='warning' icon='warning'>
                                    <Typography variant='body2'>
                                        <FormattedMessage
                                        id='api.apiCredentials.oauth.deprecate.banner'
                                        defaultMessage={'OAuth2 test credentials for the API have been deprecated, and existing keys will be discontinued soon. ' +
                                            'It is recommended to use the Test Keys available in the API Tryout for authentication.'}
                                        />
                                    </Typography>
                                </Notification>
                            </Box>
                            <TableContainer >
                                <Table className={classes.table} aria-label="simple table">
                                    <TableBody >
                                        <TableRow>
                                            <Typography className={classes.subTitle}>
                                                <FormattedMessage
                                                    id="api.apiCredentials.labels.keySecret"
                                                    defaultMessage="Credentials"
                                                />
                                            </Typography>
                                        </TableRow>
                                        {(envKey) ?
                                            /*  KeySecret Start */
                                            <>
                                                <TableRow>
                                                    <TableCell className={classes.tableCellBorder} align='left' >
                                                        <FormattedMessage
                                                            id="api.apiCredentials.labels.consumerKey"
                                                            defaultMessage="Client ID"
                                                        />
                                                    </TableCell>
                                                    <TableCell className={classes.td}>
                                                        <CopyTextField textContent={values.consumerKey} />
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell className={classes.tableCellBorder} align='left'>
                                                        <FormattedMessage
                                                            id="api.apiCredentials.labels.consumerSecret"
                                                            defaultMessage="Client Secret"
                                                        />
                                                    </TableCell>
                                                    <TableCell className={classes.td}>
                                                        <PasswordCopyField className={classes.passwordCopyField} value={values.consumerSecret} />
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell className={classes.tableCellBorder} align='left'>
                                                        <FormattedMessage
                                                            id="api.apiCredentials.labels.testToken"
                                                            defaultMessage="Test Token"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="left" className={classes.outlineButtonCell}>
                                                        <Box display="flex" flexDirection="row" mb={0.7}>
                                                            <Box mr={2}>
                                                                <GreyOutlinedButton
                                                                    text={
                                                                        intl.formatMessage({
                                                                            id: "api.apiCredentials.buttons.generateToken",
                                                                            defaultMessage: "Generate access token"
                                                                        })
                                                                    }
                                                                    onClick={handleClickTokenOpen}
                                                                    startIcon={isTokenGenerating &&
                                                                        <CircularProgress size={11} color='primary' />
                                                                    }
                                                                    className={classes.generateBtn}
                                                                    id='generate-access-token-btn'
                                                                />
                                                                {tokenOpen &&
                                                                    <AccessTokenDialog
                                                                        token={token}
                                                                        tokenOpen={tokenOpen}
                                                                        onClose={handleClickTokenClose}
                                                                        handleClickGenerate={handleClickGenerate}
                                                                        isTokenGenerating={isTokenGenerating}
                                                                        selectedPermissions={selectedPermissions}
                                                                        setPermissions={setPermissionsCallback}
                                                                        existingPermissions={permissions}
                                                                        data-testid='generate-access-token-dialog'
                                                                        generatedScopesForToken={generatedScopesForToken}
                                                                    />
                                                                }
                                                            </Box>
                                                            <Box>
                                                                <GreyOutlinedButton
                                                                    text={
                                                                        intl.formatMessage({
                                                                            id: "api.apiCredentials.buttons.curlGenerateToken",
                                                                            defaultMessage: "Curl"
                                                                        })
                                                                    }
                                                                    onClick={handleClickCurlOpen}
                                                                    className={classes.generateBtn}
                                                                    startIcon={<CustomIcons icon="curl" />}
                                                                />
                                                                {curlOpen &&
                                                                    <CurlDialog
                                                                        curlOpen={curlOpen}
                                                                        base64Encoded={base64Encoded}
                                                                        externalIDPToken=''
                                                                        tokenEndpoint={tokenEndpoint}
                                                                        onClose={handleClickCurlClose}
                                                                    />
                                                                }
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            </> :
                                            <TableRow >
                                                <TableCell
                                                    colSpan={2}
                                                    className={classes.tableCellBorder}
                                                    data-testid={'keys-info-cell'}
                                                >
                                                    <FormattedMessage
                                                        id="api.apiCredentials.messages.prodKeySecretNotGenerated"
                                                        defaultMessage="No client ID and client secret to display."
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            /* KeySecret End */
                                        }
                                        {/* EndPoint Start  */}
                                        <TableRow>
                                            <Typography className={classes.subTitle}>
                                                <FormattedMessage
                                                    id="api.apiCredentials.lables.tokenEndpoints"
                                                    defaultMessage="Token Endpoints"
                                                />
                                            </Typography>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell className={classes.tableCellBorder} align='left' >
                                                <FormattedMessage
                                                    id="api.apiCredentials.labels.tokenEndpoint"
                                                    defaultMessage="Token Endpoint"
                                                />
                                            </TableCell>
                                            <TableCell className={classes.td}>
                                                <CopyTextField
                                                    textContent={tokenEndpoint}
                                                    disabled={true}
                                                    testId="token-endpoint-value"
                                                />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell className={classes.tableCellBorder} align='left'>
                                                <FormattedMessage
                                                    id="api.apiCredentials.labels.revokeEndpoint"
                                                    defaultMessage="Revoke Endpoint"
                                                />
                                            </TableCell>
                                            <TableCell className={classes.td}>
                                                <CopyTextField textContent={revokeEndpoint} disabled={true} />
                                            </TableCell>
                                        </TableRow>
                                        {isAsgardeoKeyManagerAvailable && isResidentKeyManagerNotUsed &&
                                        <TableRow>
                                            <TableCell className={classes.tableCellBorder} align='left'>
                                                <FormattedMessage
                                                    id="components.api.apiCredentials.index.labels.authorizeEndpoint"
                                                    defaultMessage="Authorize Endpoint"
                                                />
                                            </TableCell>
                                            <TableCell className={classes.td}>
                                                <CopyTextField textContent={authorizeEndpoint} disabled={true}/>
                                            </TableCell>
                                        </TableRow>
                                        }
                                        {/* EndPoint End  */}
                                        <TableRow>
                                            <TableCell colSpan={2} className={classes.buttonCell}>
                                                {(envKey) ?
                                                    <div>
                                                        <CustomButton
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={handleClickRemoveCredentialsOpen}
                                                            data-testid='remove-creds-btn'
                                                        >
                                                            <FormattedMessage
                                                                id="api.apiCredentials.buttons.removeCredentials"
                                                                defaultMessage="Remove Credentials"
                                                            />
                                                        </CustomButton>
                                                        {removeCredentialsOpen &&
                                                            <ConfirmationDialog
                                                                open={removeCredentialsOpen}
                                                                dialogTitle={
                                                                    intl.formatMessage({
                                                                        id: "api.apiCredentials.dialogs.messages.removeCredentials",
                                                                        defaultMessage: "Remove Credentials"
                                                                    })
                                                                }
                                                                dialogContentText={
                                                                    intl.formatMessage({
                                                                        id: "api.apiCredentials.dialogs.messages.confirmDelete",
                                                                        defaultMessage: "This will remove all available credentials. Are you sure you want to remove credentials?"
                                                                    })
                                                                }
                                                                okayButtonLabel={
                                                                    intl.formatMessage({
                                                                        id: "api.apiCredentials.dialogs.buttons.okButton",
                                                                        defaultMessage: "Yes"
                                                                    })
                                                                }
                                                                cancelButtonLabel={
                                                                    intl.formatMessage({
                                                                        id: "api.apiCredentials.dialogs.buttons.cancelButton",
                                                                        defaultMessage: "No"
                                                                    })
                                                                }
                                                                onClose={handleClickRemoveCredentialsClose}
                                                                onCancel={handleClickRemoveCredentialsClose}
                                                                onOkay={handleRemoveCredentialsClick}
                                                                okButtonTestId={'remove-creds-confirmation-ok'}
                                                                cancelButtonTestId={'remove-creds-confirmation-cancel'}
                                                            />
                                                        }
                                                    </div> :
                                                    <Button
                                                        disabled={policy === ''}
                                                        disableRipple={true}
                                                        disableTouchRipple={true}
                                                        variant="contained"
                                                        color="primary"
                                                        classes={{ root: classes.generateButton }}
                                                        onClick={handleGenerateCredentialsClick}
                                                        data-testid='generate-creds-btn'
                                                    >
                                                        <FormattedMessage
                                                            id="api.apiCredentials.buttons.generateCredentials"
                                                            defaultMessage="Generate Credentials"
                                                        />
                                                    </Button>
                                                }
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Container>
            }
        </main>
    )
}

const mapDispatchToProps = {
    dispatchGenerateCreds: generateCredentials,
    dispatchUpdateAppkeys: updateAppKeys,
    dispatchRemoveCreds: removeCredentials,
    dispatchRemoveDefaultApplicationKeys: removeDefaultApplicationKeys,
    dispatchFetchIDPs: fetchIDPs,
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiInfo: state.apiReducer.api,
        subscriptionsInfo: state.subscriptionsReducer.subscriptions,
        credentialsReducer: state.credentialsReducer,
        userInfo: state.userReducer,
        idpReducer: state.idpReducer,
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(OAuthCredentials);
